import { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send, ArrowLeft, User, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export function MessagesPanel({ currentUserId }) {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => { loadConversations(); }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadConversations = async () => {
    try {
      const r = await axios.get(`${API}/messages/conversations`, { headers: getAuthHeaders() });
      setConversations(r.data || []);
    } catch (e) { console.error(e); }
  };

  const openConversation = async (conv) => {
    setActiveConversation(conv);
    try {
      const r = await axios.get(`${API}/messages/conversation/${conv.other_user_id}`, { headers: getAuthHeaders() });
      setMessages(r.data || []);
      loadConversations(); // refresh unread counts
    } catch (e) { console.error(e); }
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !activeConversation) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages`, {
        recipient_id: activeConversation.other_user_id,
        content: newMessage.trim()
      }, { headers: getAuthHeaders() });
      setNewMessage('');
      // Reload messages
      const r = await axios.get(`${API}/messages/conversation/${activeConversation.other_user_id}`, { headers: getAuthHeaders() });
      setMessages(r.data || []);
      loadConversations();
    } catch (e) { alert('Failed to send message.'); }
    finally { setSending(false); }
  };

  const totalUnread = conversations.reduce((s, c) => s + (c.unread_count || 0), 0);

  if (activeConversation) {
    return (
      <div className="flex flex-col h-[500px]" data-testid="messages-thread">
        {/* Thread Header */}
        <div className="flex items-center gap-3 pb-4 border-b border-slate-100 mb-4">
          <button onClick={() => setActiveConversation(null)} className="p-1 hover:bg-slate-100 rounded" data-testid="messages-back-btn">
            <ArrowLeft size={18} className="text-slate-500" />
          </button>
          <div className="w-8 h-8 bg-meritum-teal/10 rounded-full flex items-center justify-center">
            <User size={16} className="text-meritum-teal" />
          </div>
          <span className="font-heading font-semibold text-meritum-navy">{activeConversation.other_user_name}</span>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto space-y-3 mb-4 pr-2">
          {messages.map((msg) => {
            const isMine = msg.sender_id === currentUserId;
            return (
              <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl ${isMine ? 'bg-meritum-teal text-white rounded-br-sm' : 'bg-slate-100 text-slate-700 rounded-bl-sm'}`}>
                  <p className="font-body text-sm whitespace-pre-wrap">{msg.content}</p>
                  <p className={`font-body text-[10px] mt-1 ${isMine ? 'text-white/60' : 'text-slate-400'}`}>
                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            );
          })}
          {messages.length === 0 && <p className="text-center font-body text-slate-400 text-sm py-8">No messages yet. Start the conversation!</p>}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="flex gap-2 pt-3 border-t border-slate-100">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder="Type a message..."
            className="flex-1"
            data-testid="message-input"
          />
          <Button onClick={handleSend} disabled={sending || !newMessage.trim()} className="bg-meritum-teal text-white hover:bg-meritum-navy" data-testid="send-message-btn">
            <Send size={16} />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="messages-list">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-semibold text-meritum-navy flex items-center gap-2">
          <MessageSquare size={20} className="text-meritum-teal" />Messages
          {totalUnread > 0 && <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full font-body">{totalUnread}</span>}
        </h3>
      </div>

      {conversations.length > 0 ? (
        <div className="space-y-2">
          {conversations.map((conv) => (
            <button
              key={conv.conversation_id}
              onClick={() => openConversation(conv)}
              className="w-full text-left p-4 rounded-lg border border-slate-100 hover:bg-slate-50 transition flex items-center gap-3"
              data-testid={`conversation-${conv.other_user_id}`}
            >
              <div className="w-10 h-10 bg-meritum-teal/10 rounded-full flex items-center justify-center flex-shrink-0">
                <User size={18} className="text-meritum-teal" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="font-heading text-sm font-semibold text-meritum-navy">{conv.other_user_name}</span>
                  <span className="font-body text-[10px] text-slate-400">{new Date(conv.last_message_time).toLocaleDateString()}</span>
                </div>
                <p className="font-body text-xs text-slate-500 truncate mt-0.5">
                  {conv.is_mine ? 'You: ' : ''}{conv.last_message}
                </p>
              </div>
              {conv.unread_count > 0 && (
                <span className="w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center flex-shrink-0">{conv.unread_count}</span>
              )}
            </button>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <MessageSquare className="mx-auto text-slate-300" size={40} />
          <p className="font-body text-slate-400 mt-3">No messages yet</p>
          <p className="font-body text-xs text-slate-400 mt-1">Messages from organizations and candidates will appear here.</p>
        </div>
      )}
    </div>
  );
}

// Standalone message button for sending from org dashboard or candidate view
export function SendMessageButton({ recipientId, recipientName, className = '' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSend = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages`, { recipient_id: recipientId, content: message.trim() }, { headers: getAuthHeaders() });
      setSent(true);
      setTimeout(() => { setIsOpen(false); setSent(false); setMessage(''); }, 2000);
    } catch (e) { alert('Failed to send message.'); }
    finally { setSending(false); }
  };

  if (!isOpen) {
    return (
      <Button onClick={() => setIsOpen(true)} variant="outline" className={`border-meritum-teal text-meritum-teal hover:bg-meritum-teal hover:text-white font-body ${className}`} data-testid={`msg-btn-${recipientId}`}>
        <MessageSquare size={14} className="mr-1" />Message
      </Button>
    );
  }

  return (
    <div className="p-4 bg-white rounded-lg border border-meritum-teal/20 shadow-sm space-y-3" data-testid={`msg-form-${recipientId}`}>
      <div className="flex items-center justify-between">
        <span className="font-body text-sm font-medium text-meritum-navy">Message {recipientName}</span>
        <button onClick={() => { setIsOpen(false); setMessage(''); }} className="text-slate-400 hover:text-slate-600">✕</button>
      </div>
      {sent ? (
        <p className="font-body text-sm text-emerald-600 text-center py-2">Message sent!</p>
      ) : (
        <>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={`Write a message to ${recipientName}...`}
            rows={3}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg font-body text-sm resize-none focus:outline-none focus:ring-2 focus:ring-meritum-teal/20"
            data-testid={`msg-textarea-${recipientId}`}
          />
          <Button onClick={handleSend} disabled={sending || !message.trim()} size="sm" className="bg-meritum-teal text-white hover:bg-meritum-navy font-body w-full" data-testid={`msg-send-${recipientId}`}>
            <Send size={14} className="mr-1" />{sending ? 'Sending...' : 'Send Message'}
          </Button>
        </>
      )}
    </div>
  );
}
